import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Wine, Package, Receipt, ClipboardList, Users as UsersIcon,
  Plus, Minus, Trash2, Search, X, Save, Edit3, Download, CheckCircle2, ChefHat, Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "./AuthContext";
import { useBranding } from "./BrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INR = (n) =>
  "₹" + (Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const todayISO = () => new Date().toISOString().slice(0, 10);

const CATEGORY_OPTIONS = ["ALL", "BEER", "WHISKY", "BRANDY", "RUM", "VODKA", "WINE", "GIN", "OTHER"];

// ============ ROOT ============
export default function Bar({ onBack, onGoKitchen }) {
  const { user } = useAuth();
  const branding = useBranding();
  const [tab, setTab] = useState("sales");
  const isAdmin = user?.role === "admin";

  const tabs = [
    { id: "sales", label: "Sales", icon: Receipt },
    { id: "stock", label: "Stock", icon: Package },
    { id: "closing", label: "Closing", icon: ClipboardList },
    ...(isAdmin ? [{ id: "reports", label: "Reports", icon: Wine }] : []),
    ...(isAdmin ? [{ id: "staff", label: "Staff", icon: UsersIcon }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-slate-900 border-b border-amber-700/30 px-6 py-4 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="rounded-full text-amber-300 hover:text-amber-400 hover:bg-amber-900/30"
            data-testid="bar-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold tracking-[0.18em] text-amber-400 truncate">
              {branding.club_name} · BAR
            </h1>
            <p className="text-xs text-amber-200/70">Members-only liquor sales</p>
          </div>
          {onGoKitchen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onGoKitchen}
              className="rounded-xl text-amber-300 hover:text-amber-400 hover:bg-amber-900/30 flex-shrink-0"
              data-testid="bar-kitchen-link"
              title="Kitchen"
            >
              <ChefHat className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-[65px] z-10">
        <div className="max-w-5xl mx-auto flex overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`bar-tab-${t.id}`}
              className={`flex-shrink-0 flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {tab === "sales" && <SalesTab />}
        {tab === "stock" && <StockTab isAdmin={isAdmin} />}
        {tab === "closing" && <ClosingTab />}
        {tab === "reports" && isAdmin && <ReportsTab />}
        {tab === "staff" && isAdmin && <StaffTab currentUserId={user?.id} />}
      </div>
    </div>
  );
}

// ============ SALES TAB ============
function SalesTab() {
  const [products, setProducts] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [cart, setCart] = useState([]); // [{product_id, name, rate, qty, size_ml, current_stock}]
  const [paymentMode, setPaymentMode] = useState("cash");
  const [cashAmount, setCashAmount] = useState("");
  const [gpayAmount, setGpayAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [recentBills, setRecentBills] = useState([]);

  const loadProducts = useCallback(async () => {
    const { data } = await axios.get(`${API}/bar/products?active_only=true&limit=500`);
    setProducts(data.products || []);
  }, []);

  const loadRecent = useCallback(async () => {
    const { data } = await axios.get(`${API}/bar/sales?limit=10`);
    setRecentBills(data.sales || []);
  }, []);

  useEffect(() => { loadProducts(); loadRecent(); }, [loadProducts, loadRecent]);

  // Member search (uses existing /api/members)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (memberSearch.length < 2) { setMembers([]); return; }
      const { data } = await axios.get(`${API}/members`, { params: { search: memberSearch, limit: 8 } });
      setMembers(data.members || []);
    }, 300);
    return () => clearTimeout(t);
  }, [memberSearch]);

  const filteredProducts = useMemo(() => {
    if (!productSearch) return products.slice(0, 30);
    const s = productSearch.toLowerCase();
    return products.filter((p) =>
      p.name.toLowerCase().includes(s) || (p.barcode || "").includes(s)
    ).slice(0, 50);
  }, [productSearch, products]);

  const subtotal = useMemo(
    () => cart.reduce((sum, it) => sum + it.qty * it.rate, 0),
    [cart]
  );

  const addToCart = (p) => {
    setCart((prev) => {
      const found = prev.find((x) => x.product_id === p.id);
      if (found) {
        if (found.qty + 1 > p.current_stock) {
          toast.error(`Only ${p.current_stock} in stock for ${p.name}`);
          return prev;
        }
        return prev.map((x) =>
          x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x
        );
      }
      if (p.current_stock < 1) {
        toast.error(`${p.name} is out of stock`);
        return prev;
      }
      return [
        ...prev,
        {
          product_id: p.id,
          name: p.name,
          size_ml: p.size_ml,
          rate: p.sale_rate || 0,
          qty: 1,
          current_stock: p.current_stock,
        },
      ];
    });
  };

  const updateQty = (pid, delta) => {
    setCart((prev) =>
      prev
        .map((x) => {
          if (x.product_id !== pid) return x;
          const nq = x.qty + delta;
          if (nq > x.current_stock) {
            toast.error(`Only ${x.current_stock} in stock`);
            return x;
          }
          return { ...x, qty: nq };
        })
        .filter((x) => x.qty > 0)
    );
  };

  const updateRate = (pid, rate) => {
    setCart((prev) =>
      prev.map((x) => (x.product_id === pid ? { ...x, rate: Number(rate) || 0 } : x))
    );
  };

  const clearBill = () => {
    setCart([]);
    setSelectedMember(null);
    setMemberSearch("");
    setPaymentMode("cash");
    setCashAmount("");
    setGpayAmount("");
    setNote("");
  };

  const submitBill = async () => {
    if (!selectedMember) {
      toast.error("Select a member first");
      return;
    }
    if (cart.length === 0) {
      toast.error("Add items to the bill");
      return;
    }
    let cash = 0, gpay = 0;
    if (paymentMode === "cash") cash = subtotal;
    else if (paymentMode === "gpay") gpay = subtotal;
    else {
      cash = Number(cashAmount) || 0;
      gpay = Number(gpayAmount) || 0;
      if (Math.abs(cash + gpay - subtotal) > 0.01) {
        toast.error(`Split total must equal ${INR(subtotal)}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        member_id: selectedMember.id,
        member_number: selectedMember.member_number || "",
        member_name: selectedMember.name,
        items: cart.map((c) => ({
          product_id: c.product_id,
          qty: c.qty,
          rate: c.rate,
        })),
        payment_mode: paymentMode,
        cash_amount: cash,
        gpay_amount: gpay,
        note,
      };
      const { data } = await axios.post(`${API}/bar/sales`, payload);
      toast.success(`Bill ${data.bill_number} saved`);
      clearBill();
      loadProducts();
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save bill");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* LEFT — product picker */}
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search product / barcode..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="pl-10 h-11 rounded-xl bg-slate-50"
              data-testid="bar-product-search"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[520px] overflow-y-auto">
            {filteredProducts.map((p) => (
              <button
                key={p.id}
                onClick={() => addToCart(p)}
                disabled={p.current_stock < 1}
                data-testid={`add-product-${p.id}`}
                className={`text-left rounded-xl border p-3 transition-all ${
                  p.current_stock < 1
                    ? "bg-slate-50 border-slate-200 opacity-50 cursor-not-allowed"
                    : "bg-white border-slate-200 hover:border-amber-400 hover:shadow-md"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 leading-tight line-clamp-2">
                      {p.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {p.category} · Stock: {p.current_stock}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-base font-bold text-amber-700">{INR(p.sale_rate)}</div>
                  </div>
                </div>
              </button>
            ))}
            {filteredProducts.length === 0 && (
              <div className="col-span-full text-center text-sm text-slate-400 py-8">
                No products
              </div>
            )}
          </div>
        </div>

        {/* Recent bills */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Recent Bills</h3>
            <span className="text-xs text-slate-400">last 10</span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {recentBills.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 text-sm"
                data-testid={`recent-bill-${b.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{b.bill_number}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {b.member_name} · {b.items?.length || 0} item(s)
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-900">{INR(b.total)}</div>
                  <div className="text-xs text-slate-400">{b.payment_mode}</div>
                </div>
              </div>
            ))}
            {recentBills.length === 0 && (
              <div className="text-center text-sm text-slate-400 py-6">No bills yet</div>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT — bill / cart */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 lg:sticky lg:top-32 lg:self-start">
        <h3 className="text-base font-bold text-slate-900 mb-3">New Bill</h3>

        {/* Member picker */}
        <div className="mb-3">
          <Label className="text-xs text-slate-500 mb-1 block">Member</Label>
          {selectedMember ? (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-2.5">
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-900 truncate">{selectedMember.name}</div>
                <div className="text-xs text-slate-500">{selectedMember.member_number || "—"} · {selectedMember.mobile || ""}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => { setSelectedMember(null); setMemberSearch(""); }} data-testid="clear-member-button">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="relative">
              <Input
                placeholder="Search member name / mobile / SH..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="h-10 rounded-xl bg-slate-50"
                data-testid="bar-member-search"
              />
              {members.length > 0 && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                  {members.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedMember(m); setMemberSearch(""); setMembers([]); }}
                      data-testid={`select-member-${m.id}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 border-b border-slate-100 last:border-0"
                    >
                      <div className="font-semibold text-slate-900">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.member_number || "—"} · {m.mobile}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cart */}
        <div className="border-t border-slate-100 pt-3 space-y-2 max-h-[300px] overflow-y-auto">
          {cart.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-6">
              Tap a product to add
            </div>
          )}
          {cart.map((it) => (
            <div key={it.product_id} className="flex items-center gap-2" data-testid={`cart-item-${it.product_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-900 truncate">{it.name}</div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-slate-500">₹</span>
                  <input
                    type="number"
                    value={it.rate}
                    onChange={(e) => updateRate(it.product_id, e.target.value)}
                    className="w-16 h-6 text-xs px-1 rounded border border-slate-200 focus:outline-none focus:border-amber-400"
                    data-testid={`rate-input-${it.product_id}`}
                  />
                  <span className="text-xs text-slate-400 ml-2">= {INR(it.qty * it.rate)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => updateQty(it.product_id, -1)} data-testid={`qty-minus-${it.product_id}`}>
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="w-6 text-center text-sm font-semibold">{it.qty}</span>
                <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => updateQty(it.product_id, 1)} data-testid={`qty-plus-${it.product_id}`}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Total + Payment */}
        <div className="border-t border-slate-100 mt-3 pt-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Subtotal</span>
            <span className="text-xl font-bold text-slate-900" data-testid="bill-subtotal">{INR(subtotal)}</span>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {["cash", "gpay", "split"].map((m) => (
              <button
                key={m}
                onClick={() => setPaymentMode(m)}
                data-testid={`payment-${m}`}
                className={`py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition ${
                  paymentMode === m
                    ? "bg-slate-900 text-amber-300"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          {paymentMode === "split" && (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Cash" type="number" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} className="h-9 rounded-lg text-sm" data-testid="split-cash-input" />
              <Input placeholder="GPay" type="number" value={gpayAmount} onChange={(e) => setGpayAmount(e.target.value)} className="h-9 rounded-lg text-sm" data-testid="split-gpay-input" />
            </div>
          )}
          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-9 rounded-lg text-sm" data-testid="bill-note" />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={clearBill} data-testid="clear-bill-button">Clear</Button>
            <Button
              onClick={submitBill}
              disabled={saving || cart.length === 0 || !selectedMember}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="save-bill-button"
            >
              {saving ? "Saving..." : "Save Bill"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ STOCK TAB ============
function StockTab({ isAdmin }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [creating, setCreating] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category && category !== "ALL") params.category = category;
      const { data } = await axios.get(`${API}/bar/products`, { params });
      setProducts(data.products || []);
    } catch (e) { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  }, [search, category]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const bulkImport = async () => {
    if (!window.confirm("Import all 104 products from BASIC PRICE.xlsx? Existing barcodes will be skipped.")) return;
    try {
      const { data } = await axios.post(`${API}/bar/products/bulk-import`);
      toast.success(`Imported: ${data.added} new, ${data.skipped} existing`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search name / barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 rounded-xl bg-slate-50"
              data-testid="stock-search"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm"
            data-testid="stock-category-filter"
          >
            {CATEGORY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
          </select>
          {isAdmin && (
            <>
              <Button onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-product-button">
                <Plus className="w-4 h-4 mr-1" /> Add Product
              </Button>
              <Button variant="outline" onClick={bulkImport} data-testid="bulk-import-button">
                <Download className="w-4 h-4 mr-1" /> Bulk Import
              </Button>
            </>
          )}
        </div>
        <div className="text-xs text-slate-500">{products.length} products</div>
      </div>

      {loading && <div className="text-center text-slate-400 py-6">Loading...</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`stock-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 line-clamp-2">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.category} · {p.size_ml || "—"} ml</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-lg font-bold text-amber-700">{INR(p.sale_rate)}</div>
                <div className="text-[10px] text-slate-400 uppercase">MRP {INR(p.mrp)}</div>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-2">
              <div>
                <div className="text-xs text-slate-500">Stock</div>
                <div className={`text-xl font-bold ${p.current_stock < 5 ? "text-red-600" : "text-slate-900"}`}>
                  {p.current_stock}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setAdjustFor(p)} data-testid={`adjust-stock-${p.id}`}>
                    <Package className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingProduct(p)} data-testid={`edit-product-${p.id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editingProduct && <ProductEditModal product={editingProduct} onClose={() => setEditingProduct(null)} onSaved={load} />}
        {creating && <ProductEditModal product={null} onClose={() => setCreating(false)} onSaved={load} />}
        {adjustFor && <StockAdjustModal product={adjustFor} onClose={() => setAdjustFor(null)} onSaved={load} />}
      </AnimatePresence>
    </div>
  );
}

function ProductEditModal({ product, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState({
    barcode: product?.barcode || "",
    name: product?.name || "",
    size_ml: product?.size_ml || "",
    category: product?.category || "OTHER",
    purchase_rate: product?.purchase_rate || 0,
    sale_rate: product?.sale_rate || 0,
    mrp: product?.mrp || 0,
    current_stock: product?.current_stock || 0,
    is_active: product?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        size_ml: form.size_ml ? Number(form.size_ml) : null,
        purchase_rate: Number(form.purchase_rate) || 0,
        sale_rate: Number(form.sale_rate) || 0,
        mrp: Number(form.mrp) || 0,
        current_stock: Number(form.current_stock) || 0,
      };
      if (isNew) await axios.post(`${API}/bar/products`, payload);
      else await axios.patch(`${API}/bar/products/${product.id}`, payload);
      toast.success(isNew ? "Product added" : "Product updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await axios.delete(`${API}/bar/products/${product.id}`);
      toast.success("Deleted");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <ModalShell title={isNew ? "Add Product" : "Edit Product"} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Barcode"><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="product-barcode-input" /></Field>
          <Field label="Size (ml)"><Input type="number" value={form.size_ml} onChange={(e) => setForm({ ...form, size_ml: e.target.value })} data-testid="product-size-input" /></Field>
        </div>
        <Field label="Category">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="product-category-input">
            {CATEGORY_OPTIONS.filter((c) => c !== "ALL").map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Purchase ₹"><Input type="number" value={form.purchase_rate} onChange={(e) => setForm({ ...form, purchase_rate: e.target.value })} data-testid="product-purchase-input" /></Field>
          <Field label="Sale ₹"><Input type="number" value={form.sale_rate} onChange={(e) => setForm({ ...form, sale_rate: e.target.value })} data-testid="product-sale-input" /></Field>
          <Field label="MRP ₹"><Input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} data-testid="product-mrp-input" /></Field>
        </div>
        <Field label="Current Stock"><Input type="number" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: e.target.value })} data-testid="product-stock-input" /></Field>
        <div className="flex items-center gap-2 pt-2">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} id="prod-active" />
          <label htmlFor="prod-active" className="text-sm">Active (available for sale)</label>
        </div>
        <div className="flex gap-2 pt-3 border-t border-slate-100">
          {!isNew && <Button variant="outline" onClick={del} className="text-red-600 border-red-200" data-testid="delete-product-button"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-product-button">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function StockAdjustModal({ product, onClose, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("received");
  const [saving, setSaving] = useState(false);
  const submit = async (sign) => {
    const d = Math.abs(Number(delta) || 0) * sign;
    if (d === 0) { toast.error("Enter a quantity"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/bar/products/${product.id}/adjust-stock`, { delta: d, reason });
      toast.success(`Stock ${sign > 0 ? "added" : "removed"}: ${Math.abs(d)}`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjust failed"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={`Adjust Stock — ${product.name}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="text-xs text-slate-500">Current stock</div>
          <div className="text-3xl font-bold text-slate-900">{product.current_stock}</div>
        </div>
        <Field label="Quantity"><Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 24" data-testid="adjust-qty-input" /></Field>
        <Field label="Reason">
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="adjust-reason-select">
            <option value="received">Received (Add)</option>
            <option value="damaged">Damaged / Wastage (Remove)</option>
            <option value="correction">Manual Correction</option>
            <option value="return">Return to supplier (Remove)</option>
          </select>
        </Field>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={() => submit(-1)} disabled={saving} className="flex-1 text-red-600 border-red-200" data-testid="remove-stock-button">
            <Minus className="w-4 h-4 mr-1" /> Remove
          </Button>
          <Button onClick={() => submit(1)} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-stock-button">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ============ DAILY CLOSING ============
function ClosingTab() {
  const [date, setDate] = useState(todayISO());
  const [closing, setClosing] = useState(null);
  const [newProductsPopup, setNewProductsPopup] = useState(null); // array of created_products to price
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bar/closing/${date}`);
      setClosing(data);
    } catch (e) { toast.error("Failed to load closing"); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const updateLine = (idx, field, val) => {
    setClosing((c) => {
      const lines = [...c.stock_lines];
      lines[idx] = { ...lines[idx], [field]: Number(val) || 0 };
      const l = lines[idx];
      l.op = l.ob + l.rcd;
      l.sold = l.ob + l.rcd - l.closing;
      l.amount = Math.round(l.sold * l.rate * 100) / 100;
      return { ...c, stock_lines: lines };
    });
  };

  const updateDenom = (idx, count) => {
    setClosing((c) => {
      const d = [...c.denominations];
      d[idx] = { ...d[idx], count: Number(count) || 0, amount: (Number(count) || 0) * d[idx].note };
      // Auto-set cash = sum of all denomination amounts (physical cash count)
      const cashFromDenom = d.reduce((s, dn) => s + (Number(dn.amount) || 0), 0);
      return { ...c, denominations: d, cash: cashFromDenom };
    });
  };

  const addExpense = () => setClosing((c) => ({ ...c, expenses: [...c.expenses, { label: "", amount: 0 }] }));
  const updateExpense = (idx, field, val) => setClosing((c) => {
    const e = [...c.expenses];
    e[idx] = { ...e[idx], [field]: field === "amount" ? Number(val) || 0 : val };
    return { ...c, expenses: e };
  });
  const removeExpense = (idx) => setClosing((c) => ({ ...c, expenses: c.expenses.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => {
    if (!closing) return { total_sale: 0, total_expenses: 0, denom_total: 0, total: 0, difference: 0, matched_sum: 0, total_profit: 0 };
    const matched_sum = closing.stock_lines.reduce((s, l) => s + (l.amount || 0), 0);
    const total_sale = closing.source === "excel_upload" && closing.total_sale ? closing.total_sale : matched_sum;
    const total_profit = closing.stock_lines.reduce(
      (s, l) => s + (l.sold || 0) * ((Number(l.rate) || 0) - (Number(l.purchase_rate) || 0)),
      0
    );
    const total_expenses = closing.expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const denom_total = closing.denominations.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const cash = Number(closing.cash) || 0;
    const gpay = Number(closing.gpay) || 0;
    // Formula: Cash + GPay + Expenses
    const total = cash + gpay + total_expenses;
    // Difference: Total Sale − (Cash + GPay + Expenses)
    const difference = total_sale - total;
    return {
      total_sale: round2(total_sale),
      matched_sum: round2(matched_sum),
      total_profit: round2(total_profit),
      total_expenses: round2(total_expenses),
      denom_total: round2(denom_total),
      total: round2(total),
      difference: round2(difference),
    };
  }, [closing]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date,
        stock_lines: closing.stock_lines.map((l) => ({
          product_id: l.product_id, ob: l.ob, rcd: l.rcd, closing: l.closing, rate: l.rate,
        })),
        expenses: closing.expenses.filter((e) => e.label || e.amount),
        denominations: closing.denominations.map((d) => ({ note: d.note, count: d.count })),
        cash: Number(closing.cash) || 0,
        gpay: Number(closing.gpay) || 0,
        petty_cash: Number(closing.petty_cash) || 0,
        note: closing.note || "",
        source: closing.source || "manual",
        total_sale_override: closing.source === "excel_upload" ? Number(closing.total_sale) || null : null,
      };
      const { data } = await axios.put(`${API}/bar/closing/${date}`, payload);
      setClosing(data);
      toast.success("Closing saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading || !closing) {
    return <div className="text-center text-slate-400 py-10">Loading closing sheet...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <Label className="text-sm font-semibold">Date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 h-10" data-testid="closing-date-input" />
        <div className="flex-1" />
        <label className="inline-flex items-center gap-1 h-10 px-4 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium cursor-pointer" data-testid="upload-closing-button">
          <Upload className="w-4 h-4" /> Upload Excel
          <input type="file" accept=".xlsx,.xls" hidden onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            const fd = new FormData();
            fd.append("file", f);
            try {
              const { data } = await axios.post(`${API}/bar/closing/parse-upload?date=${date}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
              setClosing(data);
              toast.success(`Parsed ${data.stock_lines.length} items`);
              if (data.created_products?.length > 0) {
                setNewProductsPopup(data.created_products);
              }
            } catch (err) {
              toast.error(err.response?.data?.detail || "Failed to parse Excel");
            }
            e.target.value = "";
          }} />
        </label>
        <Button variant="outline" onClick={load} data-testid="sync-rates-button" title="Re-fetch latest Rate and Purchase Rate from Stock master">
          <Package className="w-4 h-4 mr-1" /> Sync from Stock
        </Button>
        <Button variant="outline" onClick={async () => {
          try {
            const res = await axios.get(`${API}/bar/reports/closing.xlsx?date=${date}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `closing_${date}.xlsx`;
            link.click();
            URL.revokeObjectURL(link.href);
          } catch (e) {
            toast.error(e.response?.data?.detail || "Save the closing first, then download");
          }
        }} data-testid="download-closing-button" title="Download closing report for this date as Excel">
          <Download className="w-4 h-4 mr-1" /> Download
        </Button>
        <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-closing-button">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Closing"}
        </Button>
      </div>

      {closing.created_products?.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm flex items-center justify-between gap-3" data-testid="created-products-alert">
          <span className="text-emerald-800">
            <strong>✓ Added to stock:</strong>{" "}
            <span className="text-emerald-700">{closing.created_products.length} new product(s) were auto-created. Set their purchase price for accurate profit.</span>
          </span>
          <Button size="sm" onClick={() => setNewProductsPopup(closing.created_products)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="open-new-products-popup">
            Set Prices
          </Button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <SummaryCard label={closing.source === "excel_upload" ? "Total Sale (Excel)" : "Total Sale"} value={totals.total_sale} accent="amber" testid="closing-total-sale" sub={closing.source === "excel_upload" && Math.abs(totals.total_sale - totals.matched_sum) > 1 ? `Matched: ${INR(totals.matched_sum)}` : null} />
        <SummaryCard label="Total Profit" value={totals.total_profit} accent="emerald" testid="closing-total-profit" />
        <SummaryCard label="Cash" value={Number(closing.cash) || 0} accent="sky" testid="closing-cash-card" />
        <SummaryCard label="GPay" value={Number(closing.gpay) || 0} accent="violet" testid="closing-gpay-card" />
        <SummaryCard label="Expenses" value={totals.total_expenses} accent="red" testid="closing-expenses" />
        <SummaryCard label="Sale" value={totals.total} accent="slate" testid="closing-total-reconcile" sub="Cash + GPay + Expenses" />
        <SummaryCard label="Difference" value={totals.difference} accent={Math.abs(totals.difference) > 1 ? "red" : "emerald"} testid="closing-difference" />
      </div>

      {/* Stock lines table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-slate-900">Stock Reconciliation</h3>
          <span className="text-xs text-slate-500">{closing.stock_lines.length} items</span>
        </div>
        <div className="px-4 py-2 border-b border-slate-100 bg-amber-50/40 text-[11px] text-amber-900 flex flex-wrap gap-x-4 gap-y-1">
          <span><b>OP</b> = OB + RCD</span>
          <span><b>Sale</b> = OP − Closing</span>
          <span><b>Value</b> = Sale × Rate</span>
          <span><b>Profit</b> = Sale × (Rate − Purch)</span>
          <span><b>Difference</b> = Total Sale − (Cash + GPay + Expenses)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Item</th>
                <th className="px-2 py-2 w-16" title="Opening Balance">OB</th>
                <th className="px-2 py-2 w-16" title="Received">RCD</th>
                <th className="px-2 py-2 w-16" title="Opening + Received (OB + RCD)">OP</th>
                <th className="px-2 py-2 w-16" title="Closing Stock">Closing</th>
                <th className="px-2 py-2 w-16" title="Sale (OP − Closing)">Sale</th>
                <th className="px-2 py-2 w-16" title="Purchase Rate">Purch</th>
                <th className="px-2 py-2 w-20" title="Rate per Unit">Rate</th>
                <th className="px-3 py-2 text-right w-24" title="Value = Sale × Rate">Value</th>
                <th className="px-3 py-2 text-right w-24" title="Profit = Sale × (Rate − Purch)">Profit</th>
              </tr>
            </thead>
            <tbody>
              {closing.stock_lines.map((l, i) => {
                const op = (Number(l.ob) || 0) + (Number(l.rcd) || 0);
                const profit = (l.sold || 0) * ((Number(l.rate) || 0) - (Number(l.purchase_rate) || 0));
                return (
                <tr key={`${l.product_id}-${i}`} className="border-t border-slate-100" data-testid={`closing-row-${l.product_id}`}>
                  <td className="px-3 py-1.5 text-xs">{l.name}</td>
                  <td><input type="number" value={l.ob} onChange={(e) => updateLine(i, "ob", e.target.value)} className="w-14 h-8 px-1 text-xs text-center rounded border border-slate-200" data-testid={`row-ob-${l.product_id}`} /></td>
                  <td><input type="number" value={l.rcd} onChange={(e) => updateLine(i, "rcd", e.target.value)} className="w-14 h-8 px-1 text-xs text-center rounded border border-slate-200" data-testid={`row-rcd-${l.product_id}`} /></td>
                  <td className="text-center text-xs font-semibold text-slate-700 bg-slate-50/70" data-testid={`row-op-${l.product_id}`}>{op}</td>
                  <td><input type="number" value={l.closing} onChange={(e) => updateLine(i, "closing", e.target.value)} className="w-14 h-8 px-1 text-xs text-center rounded border border-slate-200" data-testid={`row-closing-${l.product_id}`} /></td>
                  <td className="text-center text-xs font-semibold text-amber-700" data-testid={`row-diff-${l.product_id}`} title="Sale (units sold)">{l.sold}</td>
                  <td><input type="number" value={l.purchase_rate || 0} onChange={(e) => updateLine(i, "purchase_rate", e.target.value)} className="w-14 h-8 px-1 text-xs text-center rounded border border-slate-200 bg-slate-50" title="Purchase Rate" data-testid={`row-purchase-${l.product_id}`} /></td>
                  <td><input type="number" value={l.rate} onChange={(e) => updateLine(i, "rate", e.target.value)} className="w-16 h-8 px-1 text-xs text-center rounded border border-slate-200" data-testid={`row-rate-${l.product_id}`} /></td>
                  <td className="px-3 py-1.5 text-right font-semibold text-xs">{INR(l.amount)}</td>
                  <td className={`px-3 py-1.5 text-right font-semibold text-xs ${profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{INR(profit)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expenses + Cash */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Expenses</h3>
            <Button size="sm" variant="outline" onClick={addExpense} data-testid="add-expense-button"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
          </div>
          <div className="space-y-2">
            {closing.expenses.length === 0 && <div className="text-xs text-slate-400 text-center py-4">No expenses</div>}
            {closing.expenses.map((e, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input placeholder="Label (e.g. Salary Advance)" value={e.label} onChange={(ev) => updateExpense(i, "label", ev.target.value)} className="h-9 text-sm" data-testid={`expense-label-${i}`} />
                <Input placeholder="Amount" type="number" value={e.amount} onChange={(ev) => updateExpense(i, "amount", ev.target.value)} className="h-9 text-sm w-28" data-testid={`expense-amount-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => removeExpense(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-3 pt-3 flex justify-between text-sm">
            <span className="text-slate-500">Total Expenses</span>
            <span className="font-bold">{INR(totals.total_expenses)}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Cash / GPay / Petty</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cash Received (₹)">
              <Input type="number" value={closing.cash} onChange={(e) => setClosing({ ...closing, cash: e.target.value })} data-testid="closing-cash-input" />
              <p className="text-[10px] text-slate-400 mt-1">Auto-fills from denomination count below. Editable.</p>
            </Field>
            <Field label="GPay Amount (₹)">
              <Input type="number" value={closing.gpay} onChange={(e) => setClosing({ ...closing, gpay: e.target.value })} data-testid="closing-gpay-input" />
            </Field>
            <Field label="Petty Cash (₹)">
              <Input type="number" value={closing.petty_cash} onChange={(e) => setClosing({ ...closing, petty_cash: e.target.value })} data-testid="closing-petty-input" />
            </Field>
          </div>
          <Textarea placeholder="Notes (optional)" value={closing.note || ""} onChange={(e) => setClosing({ ...closing, note: e.target.value })} className="mt-3 min-h-[70px]" />
        </div>
      </div>

      {/* Denominations */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Denomination Count</h3>
          <div className="text-sm">
            <span className="text-slate-500">Total: </span>
            <span className="font-bold text-slate-900">{INR(totals.denom_total)}</span>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
          {closing.denominations.map((d, i) => (
            <div key={d.note} className="text-center border border-slate-200 rounded-xl p-2" data-testid={`denom-${d.note}`}>
              <div className="text-xs text-slate-500">₹{d.note}</div>
              <input
                type="number"
                value={d.count || ""}
                placeholder="0"
                onChange={(e) => updateDenom(i, e.target.value)}
                className="w-full text-center text-lg font-bold h-9 rounded border border-slate-100 focus:border-amber-400 outline-none"
                data-testid={`denom-input-${d.note}`}
              />
              <div className="text-[10px] text-slate-400 mt-0.5">{INR((d.count || 0) * d.note)}</div>
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {newProductsPopup && (
          <NewProductsPricingModal
            products={newProductsPopup}
            onClose={() => setNewProductsPopup(null)}
            onDone={() => { setNewProductsPopup(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ NEW PRODUCTS PRICING POPUP ============
function NewProductsPricingModal({ products, onClose, onDone }) {
  const [rows, setRows] = useState(() =>
    products.map((p) => ({ id: p.id, name: p.name, size_ml: p.size_ml, sale_rate: p.sale_rate || 0, purchase_rate: "" }))
  );
  const [saving, setSaving] = useState(false);
  const setRow = (i, field, val) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, [field]: val } : x)));
  const submit = async () => {
    setSaving(true);
    let updated = 0;
    for (const r of rows) {
      const pr = Number(r.purchase_rate);
      const sr = Number(r.sale_rate);
      if (pr <= 0) continue;
      try {
        await axios.patch(`${API}/bar/products/${r.id}`, { purchase_rate: pr, sale_rate: sr });
        updated++;
      } catch (e) {
        // continue with others
      }
    }
    setSaving(false);
    toast.success(`Updated ${updated} of ${rows.length} product(s)`);
    onDone();
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-900">Set Purchase Price</h3>
            <p className="text-xs text-slate-500 mt-0.5">{products.length} new product(s) from Excel. Enter purchase rate to enable accurate profit tracking.</p>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right w-24">Sale ₹</th>
                <th className="px-3 py-2 text-right w-32">Purchase ₹</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`price-row-${r.id}`}>
                  <td className="px-4 py-2">
                    <div className="text-sm font-medium text-slate-900">{r.name}</div>
                    {r.size_ml && <div className="text-[10px] text-slate-500">{r.size_ml} ml</div>}
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" value={r.sale_rate}
                      onChange={(e) => setRow(i, "sale_rate", e.target.value)}
                      className="h-9 text-sm text-right" data-testid={`price-sale-${r.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" placeholder="Enter" value={r.purchase_rate}
                      onChange={(e) => setRow(i, "purchase_rate", e.target.value)}
                      className="h-9 text-sm text-right bg-amber-50/40" data-testid={`price-purchase-${r.id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2 p-4 border-t border-slate-100 bg-slate-50">
          <p className="text-xs text-slate-500 flex-1 self-center">Rows with empty Purchase are skipped. You can always update later in the Stock tab.</p>
          <Button variant="ghost" onClick={onClose} data-testid="skip-new-products-button">Skip for now</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-new-products-button">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save Prices"}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ REPORTS TAB ============
function ReportsTab() {
  const [data, setData] = useState({ daily: [], profit: {}, by_product: [] });
  const [loading, setLoading] = useState(false);
  const [singleDate, setSingleDate] = useState(todayISO());
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [month, setMonth] = useState(todayISO().slice(0, 7)); // yyyy-mm
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/bar/reports/summary`);
        setData(data);
      } catch (e) { toast.error("Failed to load reports"); }
      finally { setLoading(false); }
    })();
  }, []);

  const download = async (url, fname) => {
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fname;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No records found for the selected range");
    }
  };
  const downloadSingle = () => download(`${API}/bar/reports/closing.xlsx?date=${singleDate}`, `closing_${singleDate}.xlsx`);
  const downloadRange = () => {
    if (!rangeFrom || !rangeTo) { toast.error("Pick both From and To dates"); return; }
    download(`${API}/bar/reports/closing.xlsx?date_from=${rangeFrom}&date_to=${rangeTo}`,
      `closing_${rangeFrom}_to_${rangeTo}.xlsx`);
  };
  const downloadMonth = () => {
    const [y, m] = month.split("-");
    download(`${API}/bar/reports/monthly.xlsx?year=${y}&month=${parseInt(m, 10)}`, `closing_${month}.xlsx`);
  };

  const totals = useMemo(() => (data.daily || []).reduce((acc, r) => ({
    sale: acc.sale + r.total_sale, cash: acc.cash + r.cash, gpay: acc.gpay + r.gpay, bills: acc.bills + r.bills,
  }), { sale: 0, cash: 0, gpay: 0, bills: 0 }), [data]);
  const profit = data.profit || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryCard label="Bill Sales" value={totals.sale} accent="amber" testid="report-total-sale" />
        <SummaryCard label="Cash" value={totals.cash} accent="emerald" />
        <SummaryCard label="GPay" value={totals.gpay} accent="sky" />
        <SummaryCard label="Bills" value={totals.bills} accent="slate" isCount />
        <SummaryCard label="Closing Sale" value={profit.total_sale_closing || 0} accent="amber" testid="report-closing-sale" />
        <SummaryCard label="Total Profit" value={profit.total_profit || 0} accent="emerald" testid="report-total-profit" />
      </div>

      {/* Download panel */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-900 mb-4">Download Reports (Excel)</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="border border-slate-200 rounded-xl p-4 flex flex-col">
            <Label className="text-xs uppercase text-slate-500 tracking-wider mb-2">Single Date</Label>
            <Input type="date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)} className="h-10 mb-3" data-testid="download-single-date" />
            <Button onClick={downloadSingle} className="bg-amber-600 hover:bg-amber-700 text-white h-10 w-full mt-auto" data-testid="download-single-button">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </div>
          <div className="border border-slate-200 rounded-xl p-4 flex flex-col">
            <Label className="text-xs uppercase text-slate-500 tracking-wider mb-2">Date Range</Label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className="h-10" placeholder="From" data-testid="download-range-from" />
              <Input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className="h-10" placeholder="To" data-testid="download-range-to" />
            </div>
            <Button onClick={downloadRange} className="bg-amber-600 hover:bg-amber-700 text-white h-10 w-full mt-auto" data-testid="download-range-button">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </div>
          <div className="border border-slate-200 rounded-xl p-4 flex flex-col">
            <Label className="text-xs uppercase text-slate-500 tracking-wider mb-2">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 mb-3" data-testid="download-month" />
            <Button onClick={downloadMonth} className="bg-amber-600 hover:bg-amber-700 text-white h-10 w-full mt-auto" data-testid="download-month-button">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Each report contains 3 sheets: Summary, Stock Details (per-product breakdown with profit), and Expenses.</p>
      </div>

      {/* Daily rollup + top-profit products */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold">Daily Bills Summary</h3></div>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-right">Bills</th><th className="px-4 py-2 text-right">Cash</th><th className="px-4 py-2 text-right">GPay</th><th className="px-4 py-2 text-right">Total</th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-6 text-slate-400">Loading...</td></tr>}
              {!loading && (data.daily || []).length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">No bill sales yet</td></tr>}
              {(data.daily || []).map((r) => (
                <tr key={r.date} className="border-t border-slate-100">
                  <td className="px-4 py-2">{r.date}</td>
                  <td className="px-4 py-2 text-right">{r.bills}</td>
                  <td className="px-4 py-2 text-right">{INR(r.cash)}</td>
                  <td className="px-4 py-2 text-right">{INR(r.gpay)}</td>
                  <td className="px-4 py-2 text-right font-semibold">{INR(r.total_sale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold">Profit by Product</h3>
            <span className="text-xs text-slate-500">from closing sheets</span>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-2 py-2 text-right">Sold</th>
                  <th className="px-2 py-2 text-right" title="Purchase Rate">Purch</th>
                  <th className="px-2 py-2 text-right" title="Sale Rate">Sale</th>
                  <th className="px-3 py-2 text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_product || []).length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-xs">Save a closing sheet with purchase rates to see profit breakdown</td></tr>}
                {(data.by_product || []).map((r) => (
                  <tr key={r.product_id} className="border-t border-slate-100" data-testid={`profit-row-${r.product_id}`}>
                    <td className="px-3 py-1.5 text-xs">{r.name}</td>
                    <td className="px-2 py-1.5 text-right text-xs">{r.sold}</td>
                    <td className="px-2 py-1.5 text-right text-xs">{INR(r.purchase_rate)}</td>
                    <td className="px-2 py-1.5 text-right text-xs">{INR(r.sale_rate)}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold text-xs ${r.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{INR(r.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ STAFF (users) ============
function StaffTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/bar/users`);
    setUsers(data.users || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-staff-button">
          <Plus className="w-4 h-4 mr-1" /> Add Staff
        </Button>
      </div>
      <div className="grid gap-2">
        {users.map((u) => (
          <div key={u.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`staff-row-${u.id}`}>
            <div className="w-10 h-10 rounded-full bg-slate-900 text-amber-300 flex items-center justify-center font-bold uppercase">
              {u.name?.[0] || u.email[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{u.name || u.email}</div>
              <div className="text-xs text-slate-500 truncate">{u.email}</div>
            </div>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${u.role === "admin" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
              {u.role}
            </span>
            <Button size="sm" variant="outline" onClick={() => setEditing(u)} data-testid={`edit-staff-${u.id}`}>
              <Edit3 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <AnimatePresence>
        {creating && <StaffEditModal user={null} onClose={() => setCreating(false)} onSaved={load} />}
        {editing && <StaffEditModal user={editing} isSelf={editing.id === currentUserId} onClose={() => setEditing(null)} onSaved={load} />}
      </AnimatePresence>
    </div>
  );
}

function StaffEditModal({ user, isSelf, onClose, onSaved }) {
  const isNew = !user;
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    role: user?.role || "bar_staff",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      if (isNew) {
        if (!form.email || !form.password) { toast.error("Email + password required"); setSaving(false); return; }
        await axios.post(`${API}/bar/users`, form);
        toast.success("Staff created");
      } else {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await axios.patch(`${API}/bar/users/${user.id}`, payload);
        toast.success("Updated");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${user.email}?`)) return;
    try {
      await axios.delete(`${API}/bar/users/${user.id}`);
      toast.success("Deleted");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <ModalShell title={isNew ? "Add Staff" : "Edit Staff"} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Email">
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!isNew} data-testid="staff-email-input" />
        </Field>
        <Field label="Name">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" />
        </Field>
        <Field label="Role">
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm" data-testid="staff-role-select">
            <option value="member_staff">Member Staff (add/view members only)</option>
            <option value="bar_staff">Bar Staff (bar sales + kitchen)</option>
            <option value="admin">Admin (full access)</option>
          </select>
        </Field>
        {user?.role === "admin" && !isNew ? (
          <Field label="New Password (leave blank to keep)">
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Type new password to change" data-testid="staff-password-input" />
            <p className="text-[11px] text-slate-500 mt-1">
              Admin password is <b>two-way synced</b> with the <b>ADMIN_PASSWORD</b> env var. Change it here OR in env → both places update. Whichever you edit last wins.
            </p>
          </Field>
        ) : (
          <Field label={isNew ? "Password" : "New Password (leave blank to keep)"}>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={isNew ? "Set initial password (min 6 chars)" : "Type new password to change"} data-testid="staff-password-input" />
            <p className="text-[11px] text-slate-500 mt-1">Only admin can set or change staff passwords. Staff cannot reset their own passwords.</p>
          </Field>
        )}
        <div className="flex gap-2 pt-3 border-t border-slate-100">
          {!isNew && !isSelf && <Button variant="outline" onClick={del} className="text-red-600 border-red-200" data-testid="delete-staff-button"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-staff-button">
            <CheckCircle2 className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ============ SHARED UTILS ============
function ModalShell({ title, children, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4">{children}</div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-500 mb-1 block uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

function SummaryCard({ label, value, accent = "slate", isCount, testid, sub }) {
  const colorMap = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
    violet: "bg-violet-50 text-violet-800 border-violet-200",
    red: "bg-red-50 text-red-800 border-red-200",
    slate: "bg-slate-50 text-slate-800 border-slate-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colorMap[accent] || colorMap.slate}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{isCount ? value : INR(value)}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
